// Type-only imports keep this free of the XState graph, so it is unit-testable on its own.
import type {
  ProStudioContext,
  ProStudioEvents,
} from '../xstate/prostudio.xstate.types';
import { mergeDeploymentHistory } from './derive-deployments';
import { selectOpenSolutionId } from './select-open-solution-id';

/**
 * The context update for `setDeploymentHistory` (PSTUDIO-4354): the Versions table rebuilt
 * from a fresh read of the deployment history.
 *
 * Three cases change nothing. A response read for a solution that is no longer open must
 * not write its rows under the one that is. A table that has not been loaded yet is left to
 * `loadBotVersions`, which reads the same history itself. And neither is a table that still
 * belongs to the PREVIOUS solution: on a switch the route names the new one at once, while
 * `versions` and `draftVersion` keep the old one's until its loader lands — `selectedBot`
 * is written by that same loader, so it says whose table this is.
 */
export const applyDeploymentHistory = (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Partial<ProStudioContext> => {
  if (event.type !== 'setDeploymentHistory') return {};
  if (!ctx.versions) return {};
  if (event.solutionId !== selectOpenSolutionId(ctx)) return {};
  if (ctx.selectedBot?.uniqueId !== event.solutionId) return {};

  return {
    versions: mergeDeploymentHistory({
      current: ctx.versions,
      history: event.deployments,
      draftVersion: ctx.draftVersion,
    }),
  };
};
